import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, ArrowRight, Mic, Video, Palette, Heart, HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SEOHead from '../components/SEOHead';

const Pricing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentLang = location.pathname.startsWith('/de') ? 'de' : 'en';

  const seoContent = {
    en: {
      title: 'Podcast Pricing Zurich | Packages from CHF 250/hour | Video & Audio Studio',
      description: 'Transparent podcast production pricing. Audio recording CHF 250/hour, video recording CHF 750/hour. Complete packages from CHF 1,850/month. Professional podcast studio in Zurich, Switzerland.',
    },
    de: {
      title: 'Podcast Preise Zürich | Pakete ab CHF 250/Stunde | Video & Audio Studio',
      description: 'Transparente Podcast-Produktionspreise. Audio-Aufnahme CHF 250/Stunde, Video-Aufnahme CHF 750/Stunde. Komplette Pakete ab CHF 1.850/Monat. Professionelles Podcast-Studio in Zürich, Schweiz.',
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": (currentLang === 'en' ? [
      {
        "@type": "Question",
        "name": "How much does it cost to record a podcast in Zurich?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Audio recording starts at CHF 250 per hour, and video recording at CHF 750 per hour. For regular productions, we offer packages starting at CHF 1,850 per month which include multiple episodes and professional editing."
        }
      },
      {
        "@type": "Question",
        "name": "What is included in a podcast production package?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our packages include studio recording time, professional audio mastering, multi-camera video recording, editing, social media clips, and thumbnail designs. The Premium package additionally includes a launch workshop, social media strategy, and monthly analytics reports."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need to bring my own equipment?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, our studio is fully equipped with professional microphones, cameras, lighting, and all necessary equipment. You just need to show up – we take care of the rest."
        }
      },
      {
        "@type": "Question",
        "name": "Can I record a video podcast?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! We specialize in video podcasts with multi-camera setup (up to 3 cameras), professional lighting, and a beautifully designed studio space. Video podcasts are ideal for YouTube, social media, and building a stronger connection with your audience."
        }
      },
      {
        "@type": "Question",
        "name": "How long does it take to produce an episode?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Recording typically takes 1-2 hours per episode. Editing and post-production require approximately 3-5 business days. With our monthly packages, we plan everything in advance to ensure consistent publishing schedules."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer live streaming and webinars?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we produce professional live streams and webinars. Our studio is equipped for live broadcasts, and we can stream to platforms like YouTube, LinkedIn, or your own website."
        }
      },
      {
        "@type": "Question",
        "name": "Where is the Podcast Zurich studio located?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our studio is located at Werdstrasse 36 in the heart of Zurich (8004). We are easily accessible by public transport and close to the main train station."
        }
      },
      {
        "@type": "Question",
        "name": "Can I visit the studio before booking?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely! We welcome you to visit our studio and meet our team. Contact us to schedule a tour, or check out our studio tour video on our website."
        }
      }
    ] : [
      {
        "@type": "Question",
        "name": "Wie viel kostet es, einen Podcast in Zürich aufzunehmen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Audio-Aufnahmen beginnen bei CHF 250 pro Stunde, Video-Aufnahmen bei CHF 750 pro Stunde. Für regelmässige Produktionen bieten wir Pakete ab CHF 1.850 pro Monat an, die mehrere Episoden und professionelles Editing beinhalten."
        }
      },
      {
        "@type": "Question",
        "name": "Was ist in einem Podcast-Produktionspaket enthalten?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Unsere Pakete umfassen Studio-Aufnahmezeit, professionelles Audio-Mastering, Video-Aufnahme mit mehreren Kameras, Editing, Social-Media-Clips und Thumbnail-Designs. Das Premium-Paket enthält zusätzlich einen Launch-Workshop, Social-Media-Strategie und monatliche Analytics-Reports."
        }
      },
      {
        "@type": "Question",
        "name": "Brauche ich eigenes Equipment für die Aufnahme?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nein, unser Studio ist voll ausgestattet mit professionellen Mikrofonen, Kameras, Beleuchtung und allem notwendigen Equipment. Sie müssen nur erscheinen – wir kümmern uns um den Rest."
        }
      },
      {
        "@type": "Question",
        "name": "Kann ich einen Video-Podcast aufnehmen?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja! Wir sind auf Video-Podcasts spezialisiert mit Multi-Kamera-Setup (bis zu 3 Kameras), professioneller Beleuchtung und einem modern eingerichteten Studio. Video-Podcasts sind ideal für YouTube, Social Media und um eine stärkere Verbindung zu Ihrem Publikum aufzubauen."
        }
      },
      {
        "@type": "Question",
        "name": "Wie lange dauert die Produktion einer Episode?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Die Aufnahme dauert typischerweise 1-2 Stunden pro Episode. Editing und Post-Produktion benötigen etwa 3-5 Werktage. Bei unseren Monatspaketen planen wir alles im Voraus, um konsistente Veröffentlichungen zu gewährleisten."
        }
      },
      {
        "@type": "Question",
        "name": "Bieten Sie auch Live-Streaming und Webinare an?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, wir produzieren professionelle Live-Streams und Webinare. Unser Studio ist für Live-Übertragungen ausgestattet, und wir können auf Plattformen wie YouTube, LinkedIn oder Ihre eigene Website streamen."
        }
      },
      {
        "@type": "Question",
        "name": "Wo befindet sich das Podcast Zürich Studio?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Unser Studio befindet sich an der Werdstrasse 36 im Herzen von Zürich (8004). Wir sind gut mit öffentlichen Verkehrsmitteln erreichbar und befinden uns in der Nähe des Hauptbahnhofs."
        }
      },
      {
        "@type": "Question",
        "name": "Kann ich eine Studio-Tour machen, bevor ich buche?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Natürlich! Wir laden Sie herzlich ein, unser Studio zu besichtigen und unser Team kennenzulernen. Kontaktieren Sie uns, um einen Besichtigungstermin zu vereinbaren, oder schauen Sie sich unser Studio-Tour-Video auf unserer Webseite an."
        }
      }
    ])
  };

  const pricingStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": seoContent[currentLang].title,
    "description": seoContent[currentLang].description,
    "url": `https://podcastzurich.com/${currentLang}/pricing`,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Audio Recording",
          "price": "250",
          "priceCurrency": "CHF",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "250",
            "priceCurrency": "CHF",
            "unitText": "hour"
          }
        },
        {
          "@type": "Offer",
          "name": "Video Recording",
          "price": "750",
          "priceCurrency": "CHF",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "750",
            "priceCurrency": "CHF",
            "unitText": "hour"
          }
        },
        {
          "@type": "Offer",
          "name": "Starter Package",
          "price": "1850",
          "priceCurrency": "CHF",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "1850",
            "priceCurrency": "CHF",
            "unitText": "month"
          }
        }
      ]
    }
  };

  const content = {
    en: {
      hero: {
        title: 'Pricing',
        subtitle: 'Transparent pricing for professional podcast production',
      },
      packages: {
        title: 'Podcast Packages',
        subtitle: 'Complete production packages to launch and grow your podcast',
        vatNote: 'All prices exclude VAT',
        items: [
          {
            name: 'Starter',
            description: 'Perfect for getting started with podcasting',
            price: 'CHF 1\'850/month',
            subPrice: 'over 6 months',
            features: [
              'Intro & Outro Recording and Editing',
              '6 recording sessions of 1 hour, more billed pro rata',
              '6 episodes editing',
              'Professional audio mastering',
              '2 cameras (1 per speaker) and 2 microphones',
            ],
            ideal: 'Ideal for testing the waters'
          },
          {
            name: 'Professional',
            description: 'For serious content creators',
            price: 'CHF 2\'000/month',
            subPrice: 'over 6 months',
            features: [
              'Intro & Outro Recording and Editing',
              '6 recording sessions of 1 hour, more billed pro rata',
              '6 episodes editing',
              'Professional audio mastering',
              '3 cameras (1 per speaker and wide shot) and 2 microphones',
              'Cameras Matching and Video Editing',
            ],
            ideal: 'Most popular choice',
            popular: true
          },
          {
            name: 'Premium',
            description: 'Complete production service',
            price: 'CHF 2\'750/month',
            subPrice: 'over 12 months',
            features: [
              'Launch Workshop with Producer and Brand Strategist',
              '12 recording sessions',
              'Professional audio mastering',
              'Cinematic video editing',
              '2 social media clips per episode',
              'Monthly analytics report',
              'Dedicated producer support',
              'Publishing to your channels',
            ],
            ideal: 'For maximum impact'
          },
        ],
      },
      services: {
        title: 'Recording & Editing Services',
        subtitle: 'Flexible hourly rates for your specific needs',
        items: [
          {
            icon: 'mic',
            title: 'Audio Recording',
            price: 'CHF 250',
            unit: 'per hour',
            description: 'Professional audio podcast recording with premium equipment',
            features: [
              'Professional studio environment',
              'High-quality microphones',
              'Raw audio tracks delivery',
              'Cloud file transfer',
            ],
          },
          {
            icon: 'video',
            title: 'Video Recording',
            price: 'CHF 750',
            unit: 'per hour',
            description: 'Multi-camera video podcast recording',
            features: [
              'Multi-camera setup (up to 3 cameras)',
              'Professional lighting',
              'Raw video files delivery',
              'Cloud file transfer',
            ],
          },
          {
            icon: 'editing',
            title: 'Editing Services',
            price: 'On Request',
            unit: '',
            description: 'Professional editing including 2D/3D animations',
            features: [
              'Audio editing & mastering',
              'Video editing & color grading',
              '2D animations & motion graphics',
              '3D animations & visual effects',
            ],
          },
        ],
      },
      custom: {
        title: 'Need a Custom Solution?',
        subtitle: 'We can create a package tailored to your specific needs. Contact us to discuss your project.',
        button: 'Request Custom Package',
      },
      community: {
        title: 'Supporting Passion Projects',
        text: 'If you are working on a personal project or something which has a positive impact and no commercial goal, get in touch and we will discuss your project and what we can do for you.',
        highlight: 'We are already supporting Sow it Goes and Mateo Gudenrath for their productions.',
        button: 'Get in Touch',
      },
      faq: {
        title: 'Frequently Asked Questions',
        subtitle: 'Answers to the most common questions about podcast production',
        items: [
          {
            question: 'How much does it cost to record a podcast in Zurich?',
            answer: 'Audio recording starts at CHF 250 per hour, and video recording at CHF 750 per hour. For regular productions, we offer packages starting at CHF 1,850 per month which include multiple episodes and professional editing.',
          },
          {
            question: 'What is included in a podcast production package?',
            answer: 'Our packages include studio recording time, professional audio mastering, multi-camera video recording, editing, social media clips, and thumbnail designs. The Premium package additionally includes a launch workshop, social media strategy, and monthly analytics reports.',
          },
          {
            question: 'Do I need to bring my own equipment?',
            answer: 'No, our studio is fully equipped with professional microphones, cameras, lighting, and all necessary equipment. You just need to show up – we take care of the rest.',
          },
          {
            question: 'Can I record a video podcast?',
            answer: 'Yes! We specialize in video podcasts with multi-camera setup (up to 3 cameras), professional lighting, and a beautifully designed studio space. Video podcasts are ideal for YouTube, social media, and building a stronger connection with your audience.',
          },
          {
            question: 'How long does it take to produce an episode?',
            answer: 'Recording typically takes 1-2 hours per episode. Editing and post-production require approximately 3-5 business days. With our monthly packages, we plan everything in advance to ensure consistent publishing schedules.',
          },
          {
            question: 'Do you offer live streaming and webinars?',
            answer: 'Yes, we produce professional live streams and webinars. Our studio is equipped for live broadcasts, and we can stream to platforms like YouTube, LinkedIn, or your own website.',
          },
          {
            question: 'Where is the Podcast Zurich studio located?',
            answer: 'Our studio is located at Werdstrasse 36 in the heart of Zurich (8004). We are easily accessible by public transport and close to the main train station.',
          },
          {
            question: 'Can I visit the studio before booking?',
            answer: 'Absolutely! We welcome you to visit our studio and meet our team. Contact us to schedule a tour, or check out our studio tour video on our website.',
          },
        ],
      },
    },
    de: {
      hero: {
        title: 'Preise',
        subtitle: 'Transparente Preise für professionelle Podcast-Produktion',
      },
      packages: {
        title: 'Podcast-Pakete',
        subtitle: 'Komplette Produktionspakete zum Starten und Wachsen Ihres Podcasts',
        vatNote: 'Alle Preise verstehen sich exkl. MwSt.',
        items: [
          {
            name: 'Starter',
            description: 'Perfekt für den Einstieg ins Podcasting',
            price: 'CHF 1\'850/Monat',
            subPrice: 'über 6 Monate',
            features: [
              'Intro & Outro Aufnahme und Bearbeitung',
              '6 Aufnahme-Sessions à 1 Stunde, weitere pro rata verrechnet',
              'Bearbeitung von 6 Episoden',
              'Professionelles Audio-Mastering',
              '2 Kameras (1 pro Sprecher) und 2 Mikrofone',
            ],
            ideal: 'Ideal zum Ausprobieren'
          },
          {
            name: 'Professional',
            description: 'Für engagierte Content-Ersteller',
            price: 'CHF 2\'000/Monat',
            subPrice: 'über 6 Monate',
            features: [
              'Intro & Outro Aufnahme und Bearbeitung',
              '6 Aufnahme-Sessions à 1 Stunde, weitere pro rata verrechnet',
              'Bearbeitung von 6 Episoden',
              'Professionelles Audio-Mastering',
              '3 Kameras (1 pro Sprecher und Wide Shot) und 2 Mikrofone',
              'Kamera-Matching und Video-Bearbeitung',
            ],
            ideal: 'Beliebteste Wahl',
            popular: true
          },
          {
            name: 'Premium',
            description: 'Kompletter Produktionsservice',
            price: 'CHF 2\'750/Monat',
            subPrice: 'über 12 Monate',
            features: [
              'Launch-Workshop mit Producer und Brand Strategist',
              '12 Aufnahme-Sessions',
              'Professionelles Audio-Mastering',
              'Filmreife Video-Bearbeitung',
              '2 Social-Media-Clips pro Episode',
              'Monatlicher Analytics-Report',
              'Dedizierter Producer-Support',
              'Veröffentlichung auf Ihren Kanälen',
            ],
            ideal: 'Für maximale Wirkung'
          },
        ],
      },
      services: {
        title: 'Aufnahme- & Editing-Services',
        subtitle: 'Flexible Stundenpreise für Ihre spezifischen Bedürfnisse',
        items: [
          {
            icon: 'mic',
            title: 'Audio-Aufnahme',
            price: 'CHF 250',
            unit: 'pro Stunde',
            description: 'Professionelle Audio-Podcast-Aufnahme mit Premium-Equipment',
            features: [
              'Professionelle Studio-Umgebung',
              'Hochwertige Mikrofone',
              'Rohe Audio-Tracks Lieferung',
              'Cloud-Dateitransfer',
            ],
          },
          {
            icon: 'video',
            title: 'Video-Aufnahme',
            price: 'CHF 750',
            unit: 'pro Stunde',
            description: 'Multi-Kamera Video-Podcast-Aufnahme',
            features: [
              'Multi-Kamera-Setup (bis zu 3 Kameras)',
              'Professionelle Beleuchtung',
              'Rohe Video-Dateien Lieferung',
              'Cloud-Dateitransfer',
            ],
          },
          {
            icon: 'editing',
            title: 'Editing-Services',
            price: 'Auf Anfrage',
            unit: '',
            description: 'Professionelles Editing inklusive 2D/3D-Animationen',
            features: [
              'Audio-Editing & Mastering',
              'Video-Editing & Farbkorrektur',
              '2D-Animationen & Motion Graphics',
              '3D-Animationen & visuelle Effekte',
            ],
          },
        ],
      },
      custom: {
        title: 'Brauchen Sie eine individuelle Lösung?',
        subtitle: 'Wir können ein Paket nach Ihren spezifischen Bedürfnissen erstellen. Kontaktieren Sie uns, um Ihr Projekt zu besprechen.',
        button: 'Individuelles Paket anfragen',
      },
      community: {
        title: 'Unterstützung für Herzensprojekte',
        text: 'Wenn Sie an einem persönlichen Projekt arbeiten oder an etwas, das einen positiven Impact hat und kein kommerzielles Ziel verfolgt, melden Sie sich bei uns. Wir besprechen gerne Ihr Projekt und was wir für Sie tun können.',
        highlight: 'Wir unterstützen bereits Sow it Goes und Mateo Gudenrath bei ihren Produktionen.',
        button: 'Kontakt aufnehmen',
      },
      faq: {
        title: 'Häufig gestellte Fragen',
        subtitle: 'Antworten auf die wichtigsten Fragen zur Podcast-Produktion',
        items: [
          {
            question: 'Wie viel kostet es, einen Podcast in Zürich aufzunehmen?',
            answer: 'Audio-Aufnahmen beginnen bei CHF 250 pro Stunde, Video-Aufnahmen bei CHF 750 pro Stunde. Für regelmässige Produktionen bieten wir Pakete ab CHF 1.850 pro Monat an, die mehrere Episoden und professionelles Editing beinhalten.',
          },
          {
            question: 'Was ist in einem Podcast-Produktionspaket enthalten?',
            answer: 'Unsere Pakete umfassen Studio-Aufnahmezeit, professionelles Audio-Mastering, Video-Aufnahme mit mehreren Kameras, Editing, Social-Media-Clips und Thumbnail-Designs. Das Premium-Paket enthält zusätzlich einen Launch-Workshop, Social-Media-Strategie und monatliche Analytics-Reports.',
          },
          {
            question: 'Brauche ich eigenes Equipment für die Aufnahme?',
            answer: 'Nein, unser Studio ist voll ausgestattet mit professionellen Mikrofonen, Kameras, Beleuchtung und allem notwendigen Equipment. Sie müssen nur erscheinen – wir kümmern uns um den Rest.',
          },
          {
            question: 'Kann ich einen Video-Podcast aufnehmen?',
            answer: 'Ja! Wir sind auf Video-Podcasts spezialisiert mit Multi-Kamera-Setup (bis zu 3 Kameras), professioneller Beleuchtung und einem modern eingerichteten Studio. Video-Podcasts sind ideal für YouTube, Social Media und um eine stärkere Verbindung zu Ihrem Publikum aufzubauen.',
          },
          {
            question: 'Wie lange dauert die Produktion einer Episode?',
            answer: 'Die Aufnahme dauert typischerweise 1-2 Stunden pro Episode. Editing und Post-Produktion benötigen etwa 3-5 Werktage. Bei unseren Monatspaketen planen wir alles im Voraus, um konsistente Veröffentlichungen zu gewährleisten.',
          },
          {
            question: 'Bieten Sie auch Live-Streaming und Webinare an?',
            answer: 'Ja, wir produzieren professionelle Live-Streams und Webinare. Unser Studio ist für Live-Übertragungen ausgestattet, und wir können auf Plattformen wie YouTube, LinkedIn oder Ihre eigene Website streamen.',
          },
          {
            question: 'Wo befindet sich das Podcast Zürich Studio?',
            answer: 'Unser Studio befindet sich an der Werdstrasse 36 im Herzen von Zürich (8004). Wir sind gut mit öffentlichen Verkehrsmitteln erreichbar und befinden uns in der Nähe des Hauptbahnhofs.',
          },
          {
            question: 'Kann ich eine Studio-Tour machen, bevor ich buche?',
            answer: 'Natürlich! Wir laden Sie herzlich ein, unser Studio zu besichtigen und unser Team kennenzulernen. Kontaktieren Sie uns, um einen Besichtigungstermin zu vereinbaren, oder schauen Sie sich unser Studio-Tour-Video auf unserer Webseite an.',
          },
        ],
      },
    },
  };

  const t = content[currentLang];

  const getIcon = (iconName) => {
    const icons = {
      mic: Mic,
      video: Video,
      editing: Palette,
    };
    const Icon = icons[iconName];
    return <Icon className="w-8 h-8" />;
  };

  // Combined structured data for both pricing and FAQ
  const combinedStructuredData = [pricingStructuredData, faqStructuredData];

  return (
    <>
      <SEOHead 
        title={seoContent[currentLang].title}
        description={seoContent[currentLang].description}
        canonicalPath={`/${currentLang}/pricing`}
        ogImage="https://customer-assets.emergentagent.com/job_podcast-hub-22/artifacts/re8fiacy_DSC05748.jpg"
        structuredData={combinedStructuredData}
      />
      <main className="min-h-screen pt-24" role="main">
        {/* Hero Section */}
        <section className="py-16 px-6 bg-gradient-to-br from-gray-100 to-gray-200" aria-labelledby="pricing-heading">
          <div className="max-w-7xl mx-auto text-center">
            <h1 id="pricing-heading" className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              {t.hero.title}
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              {t.hero.subtitle}
            </p>
          </div>
        </section>

        {/* Podcast Packages Section */}
        <section className="py-20 px-6 bg-white" aria-labelledby="packages-heading">
          <div className="max-w-7xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="packages-heading" className="text-4xl md:text-5xl font-bold mb-4">{t.packages.title}</h2>
              <p className="text-xl text-gray-600">{t.packages.subtitle}</p>
            </header>
          <div className="grid md:grid-cols-3 gap-8">
            {t.packages.items.map((pkg, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden ${pkg.popular ? 'border-2 shadow-xl' : 'border'}`}
                style={pkg.popular ? { borderColor: '#ffff31' } : {}}
                data-testid={`package-card-${pkg.name.toLowerCase()}`}
              >
                {pkg.popular && (
                  <div 
                    className="absolute top-0 right-0 px-4 py-1 text-sm font-semibold text-black"
                    style={{ backgroundColor: '#ffff31' }}
                  >
                    {pkg.ideal}
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 mb-4">{pkg.description}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, fIndex) => {
                      const isHighlight = typeof feature === 'object' && feature.highlight;
                      const featureText = isHighlight ? feature.text : feature;
                      return (
                        <li key={fIndex} className="flex items-start gap-2">
                          {isHighlight ? (
                            <Heart 
                              className="w-5 h-5 flex-shrink-0 mt-0.5" 
                              style={{ color: '#ffff31', fill: '#ffff31' }} 
                            />
                          ) : (
                            <CheckCircle2 
                              className="w-5 h-5 flex-shrink-0 mt-0.5" 
                              style={{ color: '#6b7280' }} 
                            />
                          )}
                          <span className={isHighlight ? "text-gray-900 font-semibold" : "text-gray-700"}>
                            {featureText}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {!pkg.popular && (
                    <p className="text-sm text-gray-500 italic mb-4">{pkg.ideal}</p>
                  )}
                  
                  {pkg.price && (
                    <div className="mb-4">
                      <p className="text-xl font-semibold text-gray-500">
                        {pkg.price}
                      </p>
                      {pkg.subPrice && (
                        <p className="text-xs text-gray-500 mt-1">
                          {pkg.subPrice}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <Button
                    className="w-full"
                    size="lg"
                    style={pkg.popular ? { backgroundColor: '#ffff31', color: '#000' } : {}}
                    variant={pkg.popular ? 'default' : 'outline'}
                    onClick={() => {
                      window.open('https://koalendar.com/e/podcast-session', 'koalendar-popup', 'width=700,height=700,scrollbars=yes');
                    }}
                    data-testid={`package-cta-${pkg.name.toLowerCase()}`}
                  >
                    {currentLang === 'en' ? 'Book a Call' : 'Gespräch buchen'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Equipment Video Showcase */}
          <div className="mt-16 mb-8">
            <div className="max-w-5xl mx-auto">
              <div className="rounded-xl overflow-hidden shadow-2xl bg-black">
                <iframe
                  src="https://player.vimeo.com/video/1025686779?autoplay=1&loop=1&muted=1&background=1&quality=1080p"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  style={{ width: '100%', height: '400px', display: 'block' }}
                  title="Podcast Zurich Equipment"
                ></iframe>
              </div>
              <p className="text-center text-gray-600 mt-4 text-lg">
                {currentLang === 'en' 
                  ? 'Professional equipment for exceptional productions' 
                  : 'Professionelle Ausrüstung für aussergewöhnliche Produktionen'}
              </p>
            </div>
          </div>
          
          {/* VAT Note */}
          <p className="text-center text-sm text-gray-500 mt-8 italic">
            {t.packages.vatNote}
          </p>
        </div>
      </section>

        {/* Recording & Editing Services Section */}
        <section className="py-20 px-6 bg-gradient-to-br from-gray-100 to-gray-200" aria-labelledby="services-heading">
          <div className="max-w-7xl mx-auto">
            <header className="text-center mb-16">
              <h2 id="services-heading" className="text-4xl md:text-5xl font-bold mb-4">{t.services.title}</h2>
              <p className="text-xl text-gray-600">{t.services.subtitle}</p>
            </header>
          <div className="grid md:grid-cols-3 gap-8">
            {t.services.items.map((service, index) => (
              <Card 
                key={index} 
                className="border-2 hover:shadow-xl transition-shadow bg-white"
                data-testid={`service-card-${service.icon}`}
              >
                <CardContent className="p-8">
                  <div className="mb-4" style={{ color: '#6b7280' }}>
                    {getIcon(service.icon)}
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <ul className="space-y-3 mb-6">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2">
                        <CheckCircle2 
                          className="w-5 h-5 flex-shrink-0 mt-0.5" 
                          style={{ color: '#6b7280' }} 
                        />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mb-4">
                    <p className="text-xl font-semibold text-gray-500">
                      {service.price}
                    </p>
                    {service.unit && (
                      <p className="text-sm text-gray-400">{service.unit}</p>
                    )}
                  </div>
                  <Button
                    className="w-full"
                    size="lg"
                    style={{ backgroundColor: '#ffff31', color: '#000' }}
                    onClick={() => {
                      window.open('https://koalendar.com/e/podcast-session', 'koalendar-popup', 'width=700,height=700,scrollbars=yes');
                    }}
                    data-testid={`service-cta-${service.icon}`}
                  >
                    {currentLang === 'en' ? 'Book Now' : 'Jetzt buchen'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

        {/* Custom Package CTA Section */}
        <section className="py-20 px-6 bg-gray-900 text-white" aria-labelledby="custom-heading">
          <div className="max-w-4xl mx-auto text-center">
            <h2 id="custom-heading" className="text-4xl md:text-5xl font-bold mb-6">{t.custom.title}</h2>
            <p className="text-xl text-gray-300 mb-8">{t.custom.subtitle}</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                size="lg"
                style={{ backgroundColor: '#ffff31', color: '#000' }}
                className="font-semibold hover:opacity-90 text-lg px-12"
                onClick={() => {
                  window.open('https://koalendar.com/e/podcast-session', 'koalendar-popup', 'width=700,height=700,scrollbars=yes');
                }}
                data-testid="custom-package-cta"
              >
                {currentLang === 'en' ? 'Book a Free Call' : 'Kostenloses Gespräch buchen'}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="font-semibold text-lg px-12 text-white border-white hover:bg-white hover:text-black"
                onClick={() => {
                  navigate(`/${currentLang}/contact`);
                  setTimeout(() => window.scrollTo(0, 0), 100);
                }}
                data-testid="contact-cta"
              >
                {currentLang === 'en' ? 'Contact Us' : 'Kontaktieren Sie uns'}
              </Button>
            </div>
          </div>
        </section>

        {/* Community Support Section */}
        <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-gray-100" aria-labelledby="community-heading">
          <div className="max-w-3xl mx-auto text-center">
            <article className="bg-white rounded-2xl shadow-lg p-10 border border-gray-200">
              <h3 id="community-heading" className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
                {t.community.title}
              </h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {t.community.text}
              </p>
              <p className="text-lg font-semibold mb-8" style={{ color: '#6b7280' }}>
                {t.community.highlight}
              </p>
              <Button
                variant="outline"
                size="lg"
                className="font-semibold text-lg px-10 border-2 hover:bg-gray-50"
                onClick={() => {
                  navigate(`/${currentLang}/contact`);
                  setTimeout(() => window.scrollTo(0, 0), 100);
                }}
                data-testid="community-cta"
              >
                {t.community.button}
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Button>
            </article>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-6 bg-white" aria-labelledby="faq-heading">
          <div className="max-w-4xl mx-auto">
            <header className="text-center mb-12">
              <div className="flex justify-center mb-4">
                <div className="p-3 rounded-full bg-gray-100">
                  <HelpCircle className="w-8 h-8" style={{ color: '#6b7280' }} aria-hidden="true" />
                </div>
              </div>
              <h2 id="faq-heading" className="text-4xl md:text-5xl font-bold mb-4">{t.faq.title}</h2>
              <p className="text-xl text-gray-600">{t.faq.subtitle}</p>
            </header>
            
            <Accordion type="single" collapsible className="space-y-4">
              {t.faq.items.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`} 
                  className="bg-gray-50 rounded-xl border border-gray-200 px-6 overflow-hidden"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="py-5 hover:no-underline text-left">
                    <span className="text-lg font-semibold text-gray-900 pr-4">{item.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-gray-700 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-4">
                {currentLang === 'en' 
                  ? "Still have questions? We're happy to help!" 
                  : 'Haben Sie weitere Fragen? Wir helfen Ihnen gerne!'}
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Button
                  size="lg"
                  style={{ backgroundColor: '#ffff31', color: '#000' }}
                  className="font-semibold hover:opacity-90"
                  onClick={() => {
                    window.open('https://koalendar.com/e/podcast-session', 'koalendar-popup', 'width=700,height=700,scrollbars=yes');
                  }}
                  data-testid="faq-book-call-cta"
                >
                  {currentLang === 'en' ? 'Book a Free Call' : 'Kostenloses Gespräch buchen'}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="font-semibold hover:opacity-90"
                  onClick={() => {
                    navigate(`/${currentLang}/contact`);
                    setTimeout(() => window.scrollTo(0, 0), 100);
                  }}
                  data-testid="faq-contact-cta"
                >
                  {currentLang === 'en' ? 'Contact Us' : 'Kontaktieren Sie uns'}
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Pricing;
